import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check, Circle, ListChecks } from "lucide-react";

const spring = { type: "spring", stiffness: 400, damping: 30 };

export default function PipAgendaWidget({ items = [], coverage = {} }) {
  const [expanded, setExpanded] = useState(false);
  const [openItemId, setOpenItemId] = useState(null);

  const coveredCount = items.filter((i) => coverage[i.item_id]?.covered).length;
  const total = items.length;

  if (total === 0) return null;

  return (
    <motion.div
      layout
      transition={spring}
      className="rounded-2xl border border-border/60 bg-white shadow-[0_2px_12px_-3px_rgba(0,0,0,0.08)] overflow-hidden"
    >
      {/* Header bar */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-secondary/30 transition-colors"
      >
        <ListChecks className="w-4 h-4 text-primary/60 shrink-0" />
        <span className="text-[13px] font-semibold text-foreground/80 tracking-tight">
          Agenda
        </span>

        {/* Coverage dots */}
        <div className="flex items-center gap-1 ml-1">
          {items.map((item) => (
            <motion.div
              key={item.item_id}
              layout
              className={`w-[6px] h-[6px] rounded-full transition-colors duration-500 ${
                coverage[item.item_id]?.covered ? "bg-emerald-500" : "bg-border"
              }`}
            />
          ))}
        </div>

        <span className="text-[11px] text-muted-foreground/50 font-mono tabular-nums ml-auto mr-1.5">
          {coveredCount}/{total}
        </span>

        <motion.div
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/40" />
        </motion.div>
      </button>

      {/* Expandable items list */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ ...spring, opacity: { duration: 0.15 } }}
            className="overflow-hidden"
          >
            <div className="border-t border-border/40 px-2 py-1.5 max-h-[240px] overflow-y-auto custom-scrollbar">
              {items.map((item, idx) => {
                const cov = coverage[item.item_id];
                const isCovered = cov?.covered ?? false;
                const subPoints = item.sub_points || [];
                const covSubPoints = cov?.sub_points || [];
                const notes = cov?.notes || [];
                const isOpen = openItemId === item.item_id;
                const hasDetail = subPoints.length > 0 || notes.length > 0;

                return (
                  <motion.div
                    key={item.item_id}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...spring, delay: idx * 0.03 }}
                    className="rounded-lg"
                  >
                    <button
                      onClick={() => hasDetail && setOpenItemId(isOpen ? null : item.item_id)}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${
                        hasDetail ? "hover:bg-secondary/40 cursor-pointer" : "cursor-default"
                      }`}
                    >
                      {isCovered ? (
                        <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3 text-emerald-600" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-border/30 flex items-center justify-center shrink-0">
                          <Circle className="w-2.5 h-2.5 text-muted-foreground/30" />
                        </div>
                      )}
                      <span
                        className={`text-[13px] flex-1 text-left truncate ${
                          isCovered ? "text-muted-foreground line-through decoration-muted-foreground/30" : "text-foreground/80"
                        }`}
                      >
                        {item.title}
                      </span>
                      <span className="text-[11px] text-muted-foreground/25 font-mono shrink-0">
                        {item.time_alloc_mins}m
                      </span>
                      {hasDetail && (
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          <ChevronDown className="w-3 h-3 text-muted-foreground/25" />
                        </motion.div>
                      )}
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && hasDetail && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ ...spring, opacity: { duration: 0.12 } }}
                          className="overflow-hidden"
                        >
                          <div className="ml-10 mr-2 pb-2 space-y-1">
                            {subPoints.map((sp, spi) => {
                              const byText = covSubPoints.find((c) => c.text === sp);
                              const spCovered = (byText ?? covSubPoints[spi])?.covered ?? false;
                              return (
                                <div key={spi} className="flex items-start gap-1.5">
                                  {spCovered ? (
                                    <Check className="w-3 h-3 text-emerald-500/50 mt-[3px] shrink-0" />
                                  ) : (
                                    <Circle className="w-3 h-3 text-muted-foreground/15 mt-[3px] shrink-0" />
                                  )}
                                  <span className={`text-[12px] leading-relaxed ${
                                    spCovered ? "text-muted-foreground/40" : "text-foreground/50"
                                  }`}>
                                    {sp}
                                  </span>
                                </div>
                              );
                            })}
                            {notes.length > 0 && (
                              <div className="pt-1.5 mt-1 border-t border-border/20 space-y-1">
                                {notes.map((note, ni) => (
                                  <div key={ni} className="flex items-start gap-1.5">
                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/50 mt-[5px] shrink-0" />
                                    <p className="text-[12px] text-foreground/40 leading-relaxed">{note}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
